import React from 'react';
import { Download, Trash2 } from 'lucide-react';
import {
  Badge,
  Button,
  TableCell,
  TableRow,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@roger/ui-components';
import HealWoundPopover from './HealWoundPopover';
import WoundThumbnails from './WoundThumbnails';
import type { WoundImage } from './useWoundImages';
import type { WoundResponse } from '../../services/api';
import {
  formatWoundOnset,
  getStageLabel,
  getWoundTypeLabel,
} from '../../constants/wounds';
import { contradictionAnchor } from '../oasis/contradictions/contradictionNavigation';
import {
  formatDateToMMDDYYYY,
  parseISOToLocalDate,
} from '../../utils/dateUtils';

interface WoundTableRowProps {
  wound: WoundResponse;
  assessmentCount: number;
  /** Photos logged for this wound; omitted while they are still loading. */
  images?: WoundImage[];
  /** Photos the browser cannot draw (HEIC, TIFF); the drawer offers them. */
  downloadOnlyCount?: number;
  canEdit?: boolean;
  canDiscontinue?: boolean;
  onEdit: (wound: WoundResponse) => void;
  onDelete: (wound: WoundResponse) => void;
  onDiscontinue: (
    woundId: string,
    date: Date,
    reason: string,
    comments: string
  ) => Promise<void>;
  onOpenAssessments: (wound: WoundResponse) => void;
  /** Opens the photo gallery at this position in `images`. */
  onPreviewImage?: (index: number) => void;
  /** 'row' renders a desktop table row; 'card' renders a mobile stacked card. */
  variant?: 'row' | 'card';
}

const WoundTableRow: React.FC<WoundTableRowProps> = ({
  wound,
  assessmentCount,
  images,
  downloadOnlyCount = 0,
  canEdit = true,
  canDiscontinue = false,
  onEdit,
  onDelete,
  onDiscontinue,
  onOpenAssessments,
  onPreviewImage,
  variant = 'row',
}) => {
  const isDiscontinued = !!wound.discontinued_date;
  // A wound discontinued again cannot be dated before its reactivation.
  const createdDate = new Date(wound.created_at);
  const reactivatedDate = parseISOToLocalDate(wound.reactivated_date);
  const discontinueMinDate =
    reactivatedDate && reactivatedDate > createdDate
      ? reactivatedDate
      : createdDate;

  // Row-level contradiction jump target (EMR-931). WoundsTable renders the row
  // or the card variant depending on viewport, so the anchor goes on each. The
  // navigation helper still filters to visible nodes.
  const contradictionAnchorProps = contradictionAnchor('wound_list', wound.id);

  // Shared rendering pieces reused by both the desktop row and the mobile card
  // so handlers stay identical across variants.
  const statusBadge = isDiscontinued ? (
    <Badge variant='secondary' className='text-xs'>
      Discontinued
    </Badge>
  ) : (
    <Badge
      variant='outline'
      className='text-xs bg-green-50 text-green-700 border-green-200'
    >
      Active
    </Badge>
  );

  const stageLabel = getStageLabel(wound.wound_stage, wound.wound_type);
  const deepestLabel = getStageLabel(wound.deepest_stage, wound.wound_type);
  // Only worth a line when the wound has been deeper than it is now.
  const showDeepest = !!deepestLabel && deepestLabel !== stageLabel;

  const woundDetails = (
    <div className='space-y-0.5'>
      <p
        className={
          isDiscontinued ? 'italic text-muted-foreground' : 'font-medium'
        }
      >
        {wound.location}
      </p>
      <p className='text-xs text-muted-foreground'>
        {getWoundTypeLabel(wound.wound_type)}
        {stageLabel && ` · ${stageLabel}`}
      </p>
      {showDeepest && (
        <p className='text-xs text-amber-700'>Deepest: {deepestLabel}</p>
      )}
      {wound.reactivated_date && !wound.discontinued_date && (
        <p className='text-xs text-muted-foreground'>
          Reactivated {formatDateToMMDDYYYY(wound.reactivated_date)}
        </p>
      )}
    </div>
  );

  const downloadOnlyHint =
    downloadOnlyCount === 1
      ? "1 photo can't be previewed. Open Assessments to download it."
      : `${downloadOnlyCount} photos can't be previewed. Open Assessments to download them.`;

  const thumbnails = images ? (
    <div className='flex flex-col items-start gap-1'>
      <WoundThumbnails
        images={images}
        onPreview={index => onPreviewImage?.(index)}
      />
      {downloadOnlyCount > 0 && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type='button'
                onClick={() => onOpenAssessments(wound)}
                className='inline-flex items-center gap-1 whitespace-nowrap rounded border px-1.5 py-0.5 text-xs text-muted-foreground hover:bg-muted'
              >
                <Download className='h-3 w-3' aria-hidden />
                {downloadOnlyCount} to download
                <span className='sr-only'>. {downloadOnlyHint}</span>
              </button>
            </TooltipTrigger>
            <TooltipContent className='max-w-xs'>
              <p>{downloadOnlyHint}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </div>
  ) : null;
  // A dash only once the photos have loaded and none exist in any format.
  const hasNoPhotos = images?.length === 0 && downloadOnlyCount === 0;

  const assessmentsButton = (
    <Button
      variant='outline'
      size='sm'
      onClick={() => onOpenAssessments(wound)}
    >
      Assessments ({assessmentCount})
    </Button>
  );

  const actions = (
    <div className='flex flex-wrap items-center gap-1.5'>
      {!isDiscontinued && canDiscontinue && (
        <HealWoundPopover
          minDate={discontinueMinDate}
          onDiscontinue={(date, reason, comments) =>
            onDiscontinue(wound.id, date, reason, comments)
          }
          triggerLabel='Discontinue'
        />
      )}
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span>
              <Button
                variant='outline'
                size='sm'
                onClick={() => onEdit(wound)}
                disabled={!canEdit}
              >
                Edit
              </Button>
            </span>
          </TooltipTrigger>
          {!canEdit && (
            <TooltipContent>
              <p>Cannot edit because the visit is not in an editable state</p>
            </TooltipContent>
          )}
        </Tooltip>
      </TooltipProvider>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span>
              <Button
                variant='outline'
                size='sm'
                onClick={() => onDelete(wound)}
                disabled={!canEdit || assessmentCount > 0}
              >
                <Trash2 className='h-4 w-4 text-destructive mr-1' />
                Delete
              </Button>
            </span>
          </TooltipTrigger>
          {(!canEdit || assessmentCount > 0) && (
            <TooltipContent>
              <p>
                {!canEdit
                  ? 'Cannot edit because the visit is not in an editable state'
                  : 'Cannot delete a wound with assessments'}
              </p>
            </TooltipContent>
          )}
        </Tooltip>
      </TooltipProvider>
    </div>
  );

  if (variant === 'card') {
    return (
      <div
        {...contradictionAnchorProps}
        className={`rounded-md border p-3 space-y-3 ${
          isDiscontinued ? 'opacity-60' : ''
        }`}
      >
        <div className='flex items-start justify-between gap-2'>
          <div className='min-w-0'>{woundDetails}</div>
          {statusBadge}
        </div>
        <div className='flex flex-wrap gap-x-6 gap-y-2 text-sm'>
          <div className='flex flex-col'>
            <span className='text-xs text-muted-foreground'>Onset</span>
            <span className='text-muted-foreground'>
              {formatWoundOnset(wound)}
            </span>
          </div>
          <div className='flex flex-col'>
            <span className='text-xs text-muted-foreground'>Assessments</span>
            <span>{assessmentsButton}</span>
          </div>
          {/* Same empty-vs-loading rule as the desktop Photos cell: omit the
              field while images are undefined, then show a dash once empty is
              confirmed so "no photos" is not mistaken for still loading. */}
          {images !== undefined && (
            <div className='flex flex-col gap-1'>
              <span className='text-xs text-muted-foreground'>Photos</span>
              {hasNoPhotos ? (
                <span className='text-muted-foreground'>—</span>
              ) : (
                thumbnails
              )}
            </div>
          )}
        </div>
        <div className='flex flex-wrap justify-end gap-1.5 border-t pt-2'>
          {actions}
        </div>
      </div>
    );
  }

  return (
    <TableRow
      {...contradictionAnchorProps}
      className={isDiscontinued ? 'opacity-60' : undefined}
    >
      <TableCell>{statusBadge}</TableCell>
      <TableCell className='text-sm'>{woundDetails}</TableCell>
      <TableCell>
        {/* A dash only once the photos have loaded, so an empty wound reads as
            empty rather than as still loading. */}
        {hasNoPhotos ? (
          <span className='text-muted-foreground'>—</span>
        ) : (
          thumbnails
        )}
      </TableCell>
      <TableCell className='text-sm text-muted-foreground'>
        {formatWoundOnset(wound)}
      </TableCell>
      <TableCell>{assessmentsButton}</TableCell>
      <TableCell>{actions}</TableCell>
    </TableRow>
  );
};

export default WoundTableRow;
